import { useEffect, useRef } from 'react';

// Interactive water surface: the photo is rendered through a 2D wave simulation,
// so moving the pointer (or tapping) over the hero sends ripples across it.
const SCALE = 3; // simulate at 1/3 of screen resolution, then let CSS upscale
const DAMPING = 5; // ripple lifetime (bit shift; higher = longer-lived)
const DROP_EVERY_MS = 1100; // ambient "rain" so the water is alive without input

export default function WaterRipple({ src, className = '' }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    // Older browsers (and the test environment) get the plain dark background.
    if (typeof window.ResizeObserver === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
      return undefined;
    }
    const canvas = canvasRef.current;
    const host = canvas.closest('section') || canvas.parentElement;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return undefined;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    let w = 0;
    let h = 0;
    let current;
    let previous;
    let source;
    let output;
    let raf = 0;
    let visible = true;
    let lastDrop = 0;
    let loaded = false;

    const img = new Image();

    const setup = () => {
      const rect = canvas.getBoundingClientRect();
      w = Math.max(4, Math.ceil(rect.width / SCALE));
      h = Math.max(4, Math.ceil(rect.height / SCALE));
      canvas.width = w;
      canvas.height = h;
      current = new Int16Array(w * h);
      previous = new Int16Array(w * h);

      // Paint the photo once: cover-fit, desaturated, then tinted red → ink.
      ctx.fillStyle = '#16191d';
      ctx.fillRect(0, 0, w, h);
      if (loaded) {
        const s = Math.max(w / img.width, h / img.height);
        const dw = img.width * s;
        const dh = img.height * s;
        ctx.filter = 'grayscale(1) contrast(1.2) brightness(0.8)';
        ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
        ctx.filter = 'none';
        ctx.globalCompositeOperation = 'multiply';
        const tint = ctx.createLinearGradient(0, 0, w, h);
        tint.addColorStop(0, '#d02129');
        tint.addColorStop(0.55, '#5a1c20');
        tint.addColorStop(1, '#16191d');
        ctx.fillStyle = tint;
        ctx.fillRect(0, 0, w, h);
        ctx.globalCompositeOperation = 'source-over';
      }
      source = ctx.getImageData(0, 0, w, h);
      output = ctx.createImageData(w, h);
      output.data.set(source.data);
      ctx.putImageData(source, 0, 0);
    };

    const disturb = (x, y, radius, strength) => {
      const cx = x | 0;
      const cy = y | 0;
      for (let j = cy - radius; j <= cy + radius; j++) {
        for (let i = cx - radius; i <= cx + radius; i++) {
          if (i > 0 && j > 0 && i < w - 1 && j < h - 1) {
            const idx = j * w + i;
            previous[idx] = Math.max(-4000, Math.min(4000, previous[idx] + strength));
          }
        }
      }
    };

    const step = () => {
      const len = w * h - w;
      for (let i = w; i < len; i++) {
        let v = ((previous[i - 1] + previous[i + 1] + previous[i - w] + previous[i + w]) >> 1) - current[i];
        v -= v >> DAMPING;
        current[i] = v;
      }
      const tmp = previous;
      previous = current;
      current = tmp;
    };

    const render = () => {
      const src = source.data;
      const out = output.data;
      const buf = previous;
      for (let y = 1; y < h - 1; y++) {
        for (let x = 1; x < w - 1; x++) {
          const i = y * w + x;
          const dx = buf[i - 1] - buf[i + 1];
          const dy = buf[i - w] - buf[i + w];
          let sx = x + (dx >> 4);
          let sy = y + (dy >> 4);
          if (sx < 0) sx = 0;
          else if (sx >= w) sx = w - 1;
          if (sy < 0) sy = 0;
          else if (sy >= h) sy = h - 1;
          const si = (sy * w + sx) << 2;
          const o = i << 2;
          const shade = dx >> 3;
          out[o] = src[si] + shade;
          out[o + 1] = src[si + 1] + shade;
          out[o + 2] = src[si + 2] + shade;
        }
      }
      ctx.putImageData(output, 0, 0);
    };

    const frame = (t) => {
      raf = requestAnimationFrame(frame);
      if (!visible) return;
      if (t - lastDrop > DROP_EVERY_MS) {
        lastDrop = t;
        disturb(Math.random() * w, Math.random() * h, 2, 900);
      }
      step();
      render();
    };

    const toBuffer = (e) => {
      const rect = canvas.getBoundingClientRect();
      return [(e.clientX - rect.left) / SCALE, (e.clientY - rect.top) / SCALE];
    };
    const onMove = (e) => {
      const [x, y] = toBuffer(e);
      disturb(x, y, 2, 420);
    };
    const onDown = (e) => {
      const [x, y] = toBuffer(e);
      disturb(x, y, 4, 1600);
    };

    let resizeRaf = 0;
    const resizeObserver = new ResizeObserver(() => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(setup);
    });
    const visibilityObserver = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    });

    img.onload = () => {
      loaded = true;
      setup();
    };
    img.src = src;
    setup();

    resizeObserver.observe(canvas);
    if (!reduceMotion) {
      visibilityObserver.observe(canvas);
      host.addEventListener('pointermove', onMove);
      host.addEventListener('pointerdown', onDown);
      raf = requestAnimationFrame(frame);
    }

    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(resizeRaf);
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
      host.removeEventListener('pointermove', onMove);
      host.removeEventListener('pointerdown', onDown);
      img.onload = null;
    };
  }, [src]);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
